import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, format } from "date-fns";
import { Plus, CalendarDays, MapPin, KeyRound, CheckCircle2, Map as MapIcon } from "lucide-react";
import { get } from "@/lib/api";
import { LowTokensWarning } from "@/components/TextTokens";
import { locksmithColor } from "@/lib/mapColors";
import { TeamMap } from "@/components/map/TeamMap";
import { MapsUpgradeCard } from "@/components/map/MapsUpgradeCard";
import { PinPopup } from "@/components/map/PinPopup";
import { fetchLocksmiths, fetchRuns, useAsync } from "@/lib/queries";
import { formatDate, formatTime, progressOf, todayISO } from "@/lib/constants";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { UserAvatar, useAvatarUrls } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { OwnerOverview } from "./OwnerOverview";

async function loadDashboard() {
  const today = todayISO();
  const to = format(addDays(new Date(), 7), "yyyy-MM-dd");
  const [runs, locksmiths, map] = await Promise.all([
    fetchRuns({ from: today, to }),
    fetchLocksmiths().then((ls) => ls.filter((l) => l.active && l.role === "locksmith").length),
    get("/map/today").catch(() => ({ runs: [] })),
  ]);
  return { today, runs, locksmiths, map };
}

const Stat = ({ label, value, icon: Icon, testId, accent }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${accent ? "sm:col-span-2 lg:col-span-1" : ""}`}>
    <div className="flex items-center justify-between">
      <p className="font-data text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-blue-600" />
    </div>
    <p className="font-display mt-2 text-3xl font-extrabold text-slate-900" data-testid={testId}>{value}</p>
  </div>
);

const TakenTag = ({ run }) => (run.accepted_at && run.locksmith_id ? (
  <span className="ml-2 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`dashboard-taken-${run.id}`}>Taken by {(run.locksmith?.full_name || run.locksmith?.email || "").split(" ")[0]}</span>
) : null);

const RunAvatar = ({ run }) => (run.locksmith_id ? (
  <UserAvatar userId={run.locksmith_id} stamp={run.locksmith?.avatar_updated_at} name={run.locksmith?.full_name} className="h-7 w-7 bg-slate-100 text-slate-400" testId={`dashboard-avatar-${run.id}`} />
) : null);

const groupByDate = (runs) => {
  const days = new Map();
  [...runs].sort((a, b) => a.run_date.localeCompare(b.run_date) || a.start_time.localeCompare(b.start_time))
    .forEach((r) => days.set(r.run_date, [...(days.get(r.run_date) || []), r]));
  return [...days.entries()];
};

const RunRow = ({ run }) => {
  const p = progressOf(run.run_addresses);
  return (
    <Link to={`/manager/runs/${run.id}`} data-testid={`dashboard-run-${run.id}`} className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md sm:grid-cols-[110px_1fr_1fr_180px_120px] sm:items-center">
      <span className="font-data text-sm font-semibold text-blue-800">{formatTime(run.start_time)}</span>
      <span className="flex items-center gap-2 font-semibold text-slate-900"><RunAvatar run={run} />{run.locksmith?.full_name || run.locksmith?.email || "Open job"}<TakenTag run={run} /></span>
      <span className="text-sm text-slate-600">Officer: {run.officer?.full_name}</span>
      <ProgressBar done={p.done} total={p.total} compact />
      <span className="sm:justify-self-end"><StatusBadge status={run.status} /></span>
    </Link>
  );
};

const TodayRow = ({ run, onHover }) => {
  const p = progressOf(run.run_addresses);
  return (
    <Link to={`/manager/runs/${run.id}`} data-testid={`dashboard-run-${run.id}`} onMouseEnter={() => onHover(run.id)} onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(run.id)} onBlur={() => onHover(null)}
      className="block space-y-2 rounded-lg border border-slate-200 bg-white p-4 transition-shadow hover:border-blue-300 hover:shadow-md">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: locksmithColor(run.locksmith_id) }} />
          <RunAvatar run={run} />
          <span className="truncate font-semibold text-slate-900">{run.locksmith?.full_name || run.locksmith?.email || "Open job"}</span>
          <TakenTag run={run} />
        </span>
        <StatusBadge status={run.status} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-slate-600">
        <span><span className="font-data font-semibold text-blue-800">{formatTime(run.start_time)}</span> · Officer: {run.officer?.full_name}</span>
      </div>
      <ProgressBar done={p.done} total={p.total} compact />
    </Link>
  );
};

const firstJobPoints = (mapRuns, highlight, photos) => mapRuns.flatMap((run) => {
  const a = [...run.addresses].sort((x, y) => x.position - y.position)[0];
  if (!a || a.lat == null) return [];
  return [{ key: run.id, lat: a.lat, lng: a.lng, area: a.area, boundary: a.boundary, color: locksmithColor(run.locksmith.id), pulse: a.status === "in_progress", img: photos[run.locksmith.id],
    highlighted: highlight === run.id, popup: <PinPopup run={run} address={a} title="Starting point" /> }];
});

const TodaySection = ({ data }) => {
  const [hover, setHover] = useState(null);
  const { profile } = useAuth();
  const showMap = profile?.company?.maps_enabled !== false;
  const todays = data.runs.filter((r) => r.run_date === data.today);
  const photos = useAvatarUrls(data.map.runs.map((r) => ({ id: r.locksmith.id, stamp: r.locksmith.avatar_updated_at })));
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Today · {formatDate(data.today)}</h2>
        {todays.length ? todays.map((r) => <TodayRow key={r.id} run={r} onHover={setHover} />) : (
          <EmptyState icon={CalendarDays} title="No runs scheduled today" description="Create a warrant run and assign a locksmith and officer." testId="dashboard-today-empty" />
        )}
      </div>
      {showMap && <div className="space-y-3" data-testid="dashboard-map-panel">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Team Starting Points</h2>
          <Link to="/manager/map" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline" data-testid="dashboard-full-map-link"><MapIcon className="h-4 w-4" /> Full map</Link>
        </div>
        <TeamMap points={firstJobPoints(data.map.runs, hover, photos)} className="h-[360px] lg:h-[420px]" testId="dashboard-map"
          emptyText={data.map.runs.length ? null : "No warrant runs today"} />
      </div>}
      {!showMap && <div className="space-y-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Team Starting Points</h2>
        <MapsUpgradeCard className="h-[360px] lg:h-[420px]" testId="dashboard-maps-upgrade" />
      </div>}
    </section>
  );
};

const DashboardTokenWarning = () => {
  const [t, setT] = useState(null);
  useEffect(() => {
    get("/sms-tokens").then(setT).catch(() => setT(null));
  }, []);
  return <LowTokensWarning t={t} testId="dashboard-tokens-low-warning" />;
};

const ScopeSwitch = ({ scope, onChange }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" role="group" aria-label="Dashboard scope" data-testid="dashboard-scope-switch">
    {[["mine", "My company"], ["all", "All accounts"]].map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={scope === v} data-testid={`dashboard-scope-${v}`}
        className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${scope === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{label}</button>
    ))}
  </div>
);

export default function Dashboard() {
  const { profile } = useAuth();
  const canOverview = !!profile?.is_platform_owner && !profile?.support_view;
  const [scope, setScope] = useState("mine");
  const all = canOverview && scope === "all";
  const { data, loading, error, reload } = useAsync(loadDashboard, []);
  const todays = data?.runs.filter((r) => r.run_date === data.today) || [];
  const upcoming = data?.runs.filter((r) => r.run_date !== data.today).reverse() || [];
  const todayAddresses = todays.flatMap((r) => r.run_addresses);
  const tp = progressOf(todayAddresses);

  return (
    <div className="space-y-8" data-testid="manager-dashboard">
      <PageHeader
        eyebrow={format(new Date(), "EEEE d MMMM")}
        title="Dashboard"
        description={all ? "Runs, locksmiths and addresses across every company." : "Today's warrant runs and progress across your team."}
        actions={<div className="flex flex-wrap items-center gap-2">
          {canOverview && <ScopeSwitch scope={scope} onChange={setScope} />}
          {!all && <Button asChild className="bg-blue-700 hover:bg-blue-800" data-testid="dashboard-add-run-button"><Link to="/manager/runs/new"><Plus className="mr-1 h-4 w-4" /> New warrant run</Link></Button>}
        </div>}
      />
      {all && <OwnerOverview />}
      {!all && error && <ErrorState message={error} onRetry={reload} />}
      {!all && loading && !data && <LoadingRows rows={3} />}
      {!all && <DashboardTokenWarning />}
      {!all && data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Runs today" value={todays.length} icon={CalendarDays} testId="stat-runs-today" />
            <Stat label="Addresses today" value={todayAddresses.length} icon={MapPin} testId="stat-addresses-today" />
            <Stat label="Completed today" value={`${tp.pct}%`} icon={CheckCircle2} testId="stat-completed-today" />
            <Stat label="Active Team Members" value={data.locksmiths ?? 0} icon={KeyRound} testId="stat-active-locksmiths" />
          </div>
          <TodaySection data={data} />
          <section className="space-y-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Next 7 days</h2>
            {upcoming.length ? groupByDate(upcoming).map(([date, runs]) => (
              <div key={date} className="space-y-2" data-testid={`dashboard-upcoming-day-${date}`}>
                <p className="font-data text-xs uppercase tracking-wider text-slate-500">{formatDate(date)} · {runs.length} run{runs.length === 1 ? "" : "s"}</p>
                {runs.map((r) => <RunRow key={r.id} run={r} />)}
              </div>
            )) : <p className="text-sm text-slate-500" data-testid="dashboard-upcoming-empty">Nothing scheduled for the coming week.</p>}
          </section>
        </>
      )}
    </div>
  );
}
